import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useSocket } from "../../context/SocketContext";
import { setQuizLiveGame } from "../../slices/fun_stuff/quizAuthSlice";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import FishLoader from "../FishLoader";
import TopicInput from "./TopicInput";
import LiveGameScoreboard from "./LiveGameScoreboard";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare, faSquareCheck, faPlay, faRotateLeft } from '@fortawesome/free-solid-svg-icons';

const levels = {
    'elementary': 'Elementary',
    'high_school': 'High School',
    'honors': 'Honors',
    'university': 'University',
};

const LiveGameRoom = (({ liveGroupId, groupName, setShowLiveGameRoom, liveGroupMembers }) => {
    const socket = useSocket();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const { quizLiveGame } = useSelector(state => state.quizAuth);

    const [participants, setParticipants] = useState(liveGroupMembers);
    const [playerReady, setPlayerReady] = useState(false);
    const [scoreData, setScoreData] = useState({});
    const [showFinal, setShowFinal] = useState(false);
    const [topicsRemaining, setTopicsRemaining] = useState(0);
    const [waiting, setWaiting] = useState(false);
    const [inAction, setInAction] = useState(false);

    useEffect(() => {
        if (quizLiveGame?.inAction) {
            setInAction(true);
            setWaiting(true);
        } else {
            setInAction(false);
        }
    }, [quizLiveGame?.inAction])

    useEffect(() => {
        if (location.state) {
            const { quizCompleted } = location.state;
            if (quizCompleted && !quizLiveGame.completed) {
                setWaiting(true);
                navigate(location.pathname, { 
                    replace: true, 
                    quizCompleted: false,
                });
            }
        }
    }, [location.state, location.pathname, navigate, quizLiveGame?.completed]);

    useEffect(() => {
        if (liveGroupMembers) {
            setParticipants(liveGroupMembers);
        }
    }, [liveGroupMembers]);

    useEffect(() => {
        if (quizLiveGame?.participants) {
            setParticipants(quizLiveGame.participants);
        }
    }, [quizLiveGame?.participants]);

    useEffect(() => {
        if (quizLiveGame?.completed) {
            const { currTotals, currQuiz } = quizLiveGame;
            setScoreData({
                roundCorrect: currQuiz.numberCorrect,
                roundNumQuestions: currQuiz.numberQuestions,
                roundIndividualNumQuestions: currQuiz.roundIndividualNumQuestions,
                roundHighScore: currQuiz.highScore,
                roundHighScorePlayers: currQuiz.highScorePlayers,
                totalCorrect: currTotals.totalCorrect,
                totalNumQuestions: currTotals.totalQuestions,
                totalHighScore: currTotals.currentHighScore,
                highScoreNumQuestions: quizLiveGame.totalQuestions,
                totalHighScorePlayers: currTotals.currentHighScorePlayers,
                personalNumCorrect: quizLiveGame.numCorrect,
                personalTotalCorrect: quizLiveGame.totalCorrect,
            })
            setShowFinal(true);
            setTopicsRemaining(quizLiveGame.topicsRemaining);
            setWaiting(false);
        } else {
            setShowFinal(false);
        }
    }, [quizLiveGame]);

    const leaveRoom = () => {
        socket.emit('leave_live_game_room', { groupId: liveGroupId });
        setShowLiveGameRoom(false);
        dispatch(setQuizLiveGame({}));
    }

    const togglePlayerReady = () => {
        const isReady = !playerReady;
        setPlayerReady(isReady);
        socket.emit('game_player_ready', { groupId: liveGroupId, isReady })
    }

    const submitParams = (topic, level, number) => {
        socket.emit(
            'game_add_quiz_params', { 
            groupId: liveGroupId, 
            newQuizParams: {
                topic,
                level,
                number,
            }}, (res) => {
                if (res.success) {
                    dispatch(setQuizLiveGame({ 
                    ...quizLiveGame, 
                    paramsSubmitted: true,
                    participants: res.currentMembers,
                    topicsRemaining: res.topicsRemaining,
                }));
                } else {
                    toast.error('Topic submission failed.', res.message.error);
                }
            } 
        );
    }

    const startGame = () => {
        setWaiting(false);
        socket.emit('start_live_game', { groupId: liveGroupId });
    }

    const resetGame = () => {
        setWaiting(false);
        navigate(location.pathname, { 
            replace: true, 
            quizCompleted: false,
        });
        socket.emit('reset_live_game', { groupId: liveGroupId });
    }

    return (
    <div className="modal-overlay">
        <div className="live-game-box">
            {quizLiveGame?.startActive && 
            <FishLoader 
                topic={quizLiveGame?.topic || ''}
                level={quizLiveGame ? levels[quizLiveGame.level] : ''}
                cancel={leaveRoom}
            />}

            <div className="header d-flex justify-content-center">
                <h2 className="p-0">{`${groupName} Live Game`}</h2>
            </div>

            <div className="content d-flex flex-column align-items-center">

                {inAction &&
                <p>Group is currently taking a quiz. We'll get you in on the next round</p>
                }

                {showFinal && 
                <>
                <LiveGameScoreboard scoreData={scoreData} />
                <p>{topicsRemaining > 0 ? `There are still ${topicsRemaining} more topics to play` : 'You have completed all topics. Click "Reset Game" to play again'}</p>
                </>}

                {waiting && 
                <p>Waiting on group to complete quiz...</p>}
                
                {!quizLiveGame?.paramsSubmitted &&
                <> 
                <h3 className="my-0">Enter your topic</h3>
                <div className="live-game-input">
                    <TopicInput 
                        numberDefault={5}
                        submitTopic={submitParams}
                        liveGame={true}
                    />
                </div>
                </>}
                
                <h3>Participants</h3>
                <div className="d-flex flex-column align-items-start">
                    {participants?.map(participant => (
                        <div 
                        key={participant} 
                        onClick={() => togglePlayerReady()}
                        className={`d-flex align-items-top my-1 ${participant.isReady ? 'player-ready' : participant.paramsSubmitted ? 'topic-submitted' : ''}`}
                        >
                            {participant.isReady ? <FontAwesomeIcon icon={faSquareCheck} className="me-2 ready-check"/> : 
                            <FontAwesomeIcon icon={faSquare} className="me-2 ready-check"/>}
                            <h4 className="m-0">{`${participant.username} - ${participant.isReady ? 'Ready' : participant.paramsSubmitted ? 'Topic submitted': 'No topic yet'}`}</h4>
                        </div>
                    ))}
                </div>
            </div>

            <div className="footer d-flex justify-content-center px-3">
                {/* TODO: Add "I'm not ready btn" */}
                <Button 
                    onClick={() => togglePlayerReady()}
                    className="quiz-btn"
                >
                    I'm Ready
                </Button>
                {(quizLiveGame?.topicsRemaining > 0 || !quizLiveGame?.completed) ? (
                <Button 
                    onClick={() => startGame()}
                    className="quiz-btn"
                    disabled={waiting || !(quizLiveGame?.groupReady ?? false) || Number(quizLiveGame?.topicsRemaining) <= 0 || inAction}
                >   
                    <FontAwesomeIcon icon={faPlay} className="me-2"/>
                    Start Game
                </Button>
                ) : (
                <Button 
                    onClick={() => resetGame()}
                    className="quiz-btn"
                >
                    <FontAwesomeIcon icon={faRotateLeft} className="me-2"/>
                    Reset Game
                </Button>
                )}
                <Button 
                    onClick={() => leaveRoom()}
                    className="quiz-btn m-0"
                >
                    Leave Game
                </Button>
            </div>
        </div>
    </div>
    );
})

export default LiveGameRoom